import { create } from 'zustand';
import * as callsApi from '@/api/calls';
import type { CallPreferences } from '@/api/calls';

type CallSessionStatus = 'idle' | 'inPool' | 'searching' | 'ringing' | 'active' | 'ended';

interface CallSession {
  status: CallSessionStatus;
  sessionId: string | null;
  matchScore: number | null;
  startedAt: Date | null;
  durationSeconds: number;
  endReason: string | null;
}

interface CallState {
  preferences: CallPreferences;
  session: CallSession;
  onlineCount: number;
  isLoading: boolean;
  isMuted: boolean;
  isSpeakerOn: boolean;
  error: string | null;

  // WebRTC state
  peerConnection: RTCPeerConnection | null;
  localStream: MediaStream | null;
  wsChannel: WebSocket | null;

  // Actions
  loadPreferences: () => Promise<void>;
  updatePreferences: (prefs: Partial<CallPreferences>) => Promise<void>;
  verifyAge: (birthDate: string) => Promise<void>;
  joinPool: () => Promise<void>;
  leavePool: () => Promise<void>;
  startSearch: () => void;
  cancelSearch: () => void;
  acceptCall: (sessionId: string) => Promise<void>;
  declineCall: (sessionId: string) => void;
  endCall: () => Promise<void>;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  clearError: () => void;
  cleanup: () => void;
}

// Fallback ICE servers (STUN only, used if TURN credential fetch fails)
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

let cachedIceServers: RTCIceServer[] | null = null;

async function getIceServers(): Promise<RTCIceServer[]> {
  if (cachedIceServers) return cachedIceServers;
  try {
    const servers = await callsApi.getTurnCredentials();
    cachedIceServers = servers;
    return servers;
  } catch (e) {
    console.warn('TURN credential fetch failed, using fallback STUN:', e);
    return FALLBACK_ICE_SERVERS;
  }
}

const defaultPreferences: CallPreferences = {
  status: 'offline',
  gender: null,
  seekGender: 'everyone',
  interests: [],
  mood: null,
  language: 'any',
  isAdultVerified: false,
};

const defaultSession: CallSession = {
  status: 'idle',
  sessionId: null,
  matchScore: null,
  startedAt: null,
  durationSeconds: 0,
  endReason: null,
};

let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let callTimerInterval: ReturnType<typeof setInterval> | null = null;

export const useCallStore = create<CallState>((set, get) => ({
  preferences: defaultPreferences,
  session: defaultSession,
  onlineCount: 0,
  isLoading: false,
  isMuted: false,
  isSpeakerOn: false,
  error: null,
  peerConnection: null,
  localStream: null,
  wsChannel: null,

  loadPreferences: async () => {
    set({ isLoading: true, error: null });
    try {
      const prefs = await callsApi.getCallPreferences();
      const { onlineCount } = await callsApi.getOnlineCount();
      set({ preferences: prefs, onlineCount, isLoading: false });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load preferences';
      set({ isLoading: false, error: message });
    }
  },

  updatePreferences: async (prefs) => {
    set({ isLoading: true, error: null });
    try {
      const updated = await callsApi.updateCallPreferences(prefs);
      set({ preferences: updated, isLoading: false });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update preferences';
      set({ isLoading: false, error: message });
    }
  },

  verifyAge: async (birthDate) => {
    set({ isLoading: true, error: null });
    try {
      await callsApi.verifyCallAge(birthDate);
      set((state) => ({
        preferences: { ...state.preferences, isAdultVerified: true },
        isLoading: false,
      }));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Age verification failed';
      set({ isLoading: false, error: message });
    }
  },

  joinPool: async () => {
    try {
      const { onlineCount } = await callsApi.joinCallPool();
      set((state) => ({
        session: { ...state.session, status: 'inPool' },
        onlineCount,
      }));
      connectWebSocket(get, set);
      startHeartbeat(get);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to join pool';
      set({ error: message });
    }
  },

  leavePool: async () => {
    stopHeartbeat();
    disconnectWebSocket(get);
    try {
      await callsApi.leaveCallPool();
    } catch {
      // Ignore errors on leave
    }
    set({ session: { ...defaultSession, status: 'idle' } });
  },

  startSearch: () => {
    set((state) => ({
      session: { ...state.session, status: 'searching' },
    }));
    sendWebSocketMessage(get, { type: 'start_search' });
  },

  cancelSearch: () => {
    sendWebSocketMessage(get, { type: 'cancel_search' });
    set((state) => ({
      session: { ...state.session, status: 'inPool' },
    }));
  },

  acceptCall: async (sessionId) => {
    sendWebSocketMessage(get, { type: 'accept_call', session_id: sessionId });
    await initializeWebRTC(get, set);
    set((state) => ({
      session: { ...state.session, status: 'active' },
    }));
  },

  declineCall: (sessionId) => {
    sendWebSocketMessage(get, { type: 'decline_call', session_id: sessionId });
    set((state) => ({
      session: { ...state.session, status: 'inPool' },
    }));
  },

  endCall: async () => {
    stopCallTimer();
    const { session } = get();
    if (session.sessionId) {
      sendWebSocketMessage(get, { type: 'end_call', session_id: session.sessionId });
    }
    await cleanupWebRTC(get, set);
    set((state) => ({
      session: { ...state.session, status: 'inPool', sessionId: null },
      isMuted: false,
      isSpeakerOn: false,
    }));
  },

  toggleMute: () => {
    const { localStream, isMuted } = get();
    const newMuted = !isMuted;
    localStream?.getAudioTracks().forEach((track) => {
      track.enabled = !newMuted;
    });
    set({ isMuted: newMuted });
  },

  toggleSpeaker: () => {
    // Note: Web doesn't have direct speaker control like mobile
    // This is a UI toggle only
    set((state) => ({ isSpeakerOn: !state.isSpeakerOn }));
  },

  clearError: () => set({ error: null }),

  cleanup: () => {
    stopHeartbeat();
    stopCallTimer();
    disconnectWebSocket(get);
    cleanupWebRTC(get, set);
    set({
      session: defaultSession,
      peerConnection: null,
      localStream: null,
      wsChannel: null,
    });
  },
}));

// Helper functions

function connectWebSocket(
  get: () => CallState,
  set: (partial: Partial<CallState> | ((state: CallState) => Partial<CallState>)) => void
) {
  const url = callsApi.getCallSignalUrl();
  const ws = new WebSocket(url);

  ws.onopen = () => {
    console.log('WebSocket connected');
  };

  ws.onmessage = async (event) => {
    const msg = JSON.parse(event.data);
    await handleWebSocketMessage(msg, get, set);
  };

  ws.onerror = (error) => {
    console.error('WebSocket error:', error);
    set({ error: 'Connection error' });
  };

  ws.onclose = () => {
    const { session } = get();
    if (session.status !== 'idle') {
      // Reconnect after delay
      setTimeout(() => {
        if (get().session.status !== 'idle') {
          connectWebSocket(get, set);
        }
      }, 2000);
    }
  };

  set({ wsChannel: ws });
}

function disconnectWebSocket(get: () => CallState) {
  const { wsChannel } = get();
  if (wsChannel) {
    wsChannel.close();
  }
}

function sendWebSocketMessage(get: () => CallState, message: Record<string, unknown>) {
  const { wsChannel } = get();
  if (wsChannel && wsChannel.readyState === WebSocket.OPEN) {
    wsChannel.send(JSON.stringify(message));
  }
}

async function handleWebSocketMessage(
  msg: Record<string, unknown>,
  get: () => CallState,
  set: (partial: Partial<CallState> | ((state: CallState) => Partial<CallState>)) => void
) {
  const type = msg.type as string;

  switch (type) {
    case 'connected':
      set({ onlineCount: (msg.online_count as number) || 0 });
      break;

    case 'online_count':
      set({ onlineCount: (msg.count as number) || 0 });
      break;

    case 'searching':
      set((state) => ({
        session: { ...state.session, status: 'searching' },
      }));
      break;

    case 'match_found':
      set((state) => ({
        session: {
          ...state.session,
          status: 'ringing',
          sessionId: msg.session_id as string,
          matchScore: msg.match_score as number | null,
        },
      }));
      await initializeWebRTC(get, set);
      await createOffer(get);
      break;

    case 'incoming_call':
      set((state) => ({
        session: {
          ...state.session,
          status: 'ringing',
          sessionId: msg.session_id as string,
        },
      }));
      break;

    case 'call_accepted':
      set((state) => ({
        session: { ...state.session, status: 'active' },
      }));
      break;

    case 'call_declined':
      await cleanupWebRTC(get, set);
      set((state) => ({
        session: { ...state.session, status: 'inPool', sessionId: null },
      }));
      break;

    case 'call_started':
      startCallTimer(set);
      set((state) => ({
        session: {
          ...state.session,
          status: 'active',
          startedAt: new Date(),
        },
      }));
      break;

    case 'call_ended':
      stopCallTimer();
      await cleanupWebRTC(get, set);
      set({
        session: {
          status: 'ended',
          sessionId: null,
          matchScore: null,
          startedAt: null,
          durationSeconds: (msg.duration as number) || 0,
          endReason: msg.reason as string | null,
        },
      });
      // Reset to pool after delay
      setTimeout(() => {
        set((state) => ({
          session: { ...state.session, status: 'inPool' },
        }));
      }, 2000);
      break;

    case 'no_match':
      set((state) => ({
        session: { ...state.session, status: 'inPool' },
      }));
      break;

    case 'offer':
      await handleOffer(msg.sdp as string, get, set);
      break;

    case 'answer':
      await handleAnswer(msg.sdp as string, get);
      break;

    case 'ice_candidate':
      await handleIceCandidate(msg.candidate as string, get);
      break;

    case 'error':
      set({ error: msg.message as string });
      break;
  }
}

function startHeartbeat(get: () => CallState) {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    sendWebSocketMessage(get, { type: 'heartbeat' });
  }, 30000);
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

function startCallTimer(
  set: (partial: Partial<CallState> | ((state: CallState) => Partial<CallState>)) => void
) {
  stopCallTimer();
  callTimerInterval = setInterval(() => {
    set((state) => ({
      session: {
        ...state.session,
        durationSeconds: state.session.durationSeconds + 1,
      },
    }));
  }, 1000);
}

function stopCallTimer() {
  if (callTimerInterval) {
    clearInterval(callTimerInterval);
    callTimerInterval = null;
  }
}

// WebRTC functions

async function initializeWebRTC(
  get: () => CallState,
  set: (partial: Partial<CallState> | ((state: CallState) => Partial<CallState>)) => void
) {
  const iceServers = await getIceServers();
  const config: RTCConfiguration = {
    iceServers,
  };

  const pc = new RTCPeerConnection(config);

  // Get audio stream
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: false,
  });

  // Add tracks to peer connection
  stream.getAudioTracks().forEach((track) => {
    pc.addTrack(track, stream);
  });

  // Handle ICE candidates
  pc.onicecandidate = (event) => {
    if (event.candidate && get().session.sessionId) {
      sendWebSocketMessage(get, {
        type: 'ice_candidate',
        session_id: get().session.sessionId,
        candidate: event.candidate.candidate,
      });
    }
  };

  // Handle remote stream
  pc.ontrack = (event) => {
    // Play remote audio
    const audio = new Audio();
    audio.srcObject = event.streams[0];
    audio.play();
  };

  set({ peerConnection: pc, localStream: stream });
}

async function createOffer(get: () => CallState) {
  const { peerConnection, session } = get();
  if (!peerConnection || !session.sessionId) return;

  const offer = await peerConnection.createOffer();
  await peerConnection.setLocalDescription(offer);

  sendWebSocketMessage(get, {
    type: 'offer',
    session_id: session.sessionId,
    sdp: offer.sdp,
  });
}

async function handleOffer(
  sdp: string,
  get: () => CallState,
  set: (partial: Partial<CallState> | ((state: CallState) => Partial<CallState>)) => void
) {
  let { peerConnection } = get();
  if (!peerConnection) {
    await initializeWebRTC(get, set);
    peerConnection = get().peerConnection;
  }
  if (!peerConnection) return;

  await peerConnection.setRemoteDescription(
    new RTCSessionDescription({ type: 'offer', sdp })
  );

  const answer = await peerConnection.createAnswer();
  await peerConnection.setLocalDescription(answer);

  sendWebSocketMessage(get, {
    type: 'answer',
    session_id: get().session.sessionId,
    sdp: answer.sdp,
  });
}

async function handleAnswer(sdp: string, get: () => CallState) {
  const { peerConnection } = get();
  if (peerConnection) {
    await peerConnection.setRemoteDescription(
      new RTCSessionDescription({ type: 'answer', sdp })
    );
  }
}

async function handleIceCandidate(candidate: string, get: () => CallState) {
  const { peerConnection } = get();
  if (peerConnection) {
    await peerConnection.addIceCandidate(
      new RTCIceCandidate({ candidate, sdpMid: '', sdpMLineIndex: 0 })
    );
  }
}

async function cleanupWebRTC(
  get: () => CallState,
  set: (partial: Partial<CallState> | ((state: CallState) => Partial<CallState>)) => void
) {
  const { localStream, peerConnection } = get();

  localStream?.getTracks().forEach((track) => track.stop());
  peerConnection?.close();

  set({ localStream: null, peerConnection: null });
}
